/* Progressive presentation refinements for explicitly native product forms. */
(function (global, document) {
  'use strict';
  if (global.FSNativeProductPresentation) return;

  const FORM_SELECTOR = 'form[data-fs-native-mode="native"]';
  const VALUE_MARKERS = [
    '[data-fs-style-name]', '[data-fs-pattern-name]', '[data-fs-super-name]',
    '[data-fs-super-pattern-name]', '[data-fs-finish-name]', '[data-fs-dog-style-name]',
    '[data-fs-layout-choice]', '[data-fs-colour-name]', '[data-fs-cotton-style-name]',
    '[data-fs-cotton-count-name]', '[data-fs-cotton-colour-name]', '[data-fs-mens-pj-layout-name]',
    '[data-fs-mens-tee-layout-name]', '[data-fs-mens-tee-colour-name]', '[data-fs-goods-selected]',
    '.fs-bulk__selected', '.fs-portrait__selected',
    '.fs-hoodie__choice-name', '.fs-ownhoodie__choice-name', '.fs-snugrem__selected',
    '.fs-apparel__choice-name', '.fs-rashie__layout-choice', '.fs-rashie__colour-name', '.fs-goods__selected',
  ].join(',');
  const PALETTE_CLASSES = [
    'fs-hoodie__colours', 'fs-ownhoodie__colours', 'fs-snugrem__colours',
    'fs-rashie__colours', 'fs-goods__colours', 'fs-apparel__colours',
  ];
  const PALETTE_SELECTOR = PALETTE_CLASSES.map(name => `.${name}`).join(',');
  const PROCESSED_LEGEND = 'data-fs-native-choice-processed';
  const PROCESSED_PALETTE = 'data-fs-native-palette-processed';
  const PROCESSED_SIZE = 'data-fs-native-size-processed';
  const PROCESSED_FEE = 'data-fs-native-fee-explanation-hidden';
  const formObservers = new WeakMap();
  const sizeSourceAttributes = new WeakMap();
  const paletteResizeObservers = new WeakMap();
  const paletteMeasureKeys = new WeakMap();
  const descriptions = new WeakSet();
  const SNUG_DESCRIPTION_IDS = new Set(['6686283956269','6793210560557','6580810186797','7072090357805','7081262612525']);
  const DESCRIPTION_SELECTOR = '.t4s-rte.t4s-tab-content[id^="t4s-tab-des"]';
  let started = false;
  let generatedId = 0;

  function uniqueId(prefix) {
    let value;
    do {
      generatedId += 1;
      value = `${prefix}-${generatedId}`;
    } while (document.getElementById(value));
    return value;
  }

  function selectedValueNode(legend) {
    return [...legend.children].find(node => node.matches(VALUE_MARKERS)) || null;
  }

  function simplifyLegend(legend) {
    if (legend.hasAttribute(PROCESSED_LEGEND)) return;
    const value = selectedValueNode(legend);
    // Some family scripts populate this value only after initialization. Keep
    // the useful prompt visible until there is a real selected value to show.
    if (!value || !value.textContent.trim()) return;
    legend.setAttribute(PROCESSED_LEGEND, '');
    value.classList.add('fs-native-choice-value');
    legend.querySelectorAll(':scope > [aria-hidden="true"]').forEach(node => {
      if (node.textContent.trim() === '*') node.classList.add('fs-native-choice-required');
    });
    for (const node of [...legend.childNodes]) {
      if (node === value || node.nodeType !== global.Node.TEXT_NODE || !node.textContent.trim()) continue;
      const context = document.createElement('span');
      context.className = 'fs-native-choice-context';
      context.textContent = node.textContent;
      legend.replaceChild(context, node);
    }
  }

  function hideFeeExplanation(node) {
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    const isFeeExplanation = /extra\s+per\s+item/i.test(text) && /charged\s+as\s+(?:a\s+)?separate\s+artwork\s+fees?/i.test(text);
    if (isFeeExplanation && !node.hasAttribute(PROCESSED_FEE)) node.setAttribute(PROCESSED_FEE, '');
    else if (!isFeeExplanation && node.hasAttribute(PROCESSED_FEE)) node.removeAttribute(PROCESSED_FEE);
  }

  function feeCandidates(form) {
    return [...form.querySelectorAll('p,[data-fs-surcharge],[data-fs-price-note]')];
  }

  function updateSizeSummary(wrapper, summary) {
    const source = [...wrapper.querySelectorAll('[data-fs-size-standard],[data-fs-size-premium]')]
      .find(select => !select.hidden && !select.disabled);
    const option = source?.selectedOptions?.[0];
    const valueNode = summary.querySelector('.fs-native-size-summary__value');
    const nextValue = option?.textContent?.trim() || option?.value?.trim() || '';
    if (valueNode && valueNode.textContent !== nextValue) valueNode.textContent = nextValue;
    summary.hidden = !nextValue;
  }

  function makeSizeSourceAccessibleToSummary(select) {
    if (!sizeSourceAttributes.has(select)) {
      sizeSourceAttributes.set(select, {
        tabindex:select.getAttribute('tabindex'),
        ariaHidden:select.getAttribute('aria-hidden'),
      });
    }
    if (select.getAttribute('tabindex') !== '-1') select.setAttribute('tabindex', '-1');
    if (select.getAttribute('aria-hidden') !== 'true') select.setAttribute('aria-hidden', 'true');
  }

  function restoreSizeSourceAccessibility(select) {
    const original = sizeSourceAttributes.get(select);
    if (!original) return;
    if (original.tabindex === null) select.removeAttribute('tabindex');
    else if (select.getAttribute('tabindex') !== original.tabindex) select.setAttribute('tabindex', original.tabindex);
    if (original.ariaHidden === null) select.removeAttribute('aria-hidden');
    else if (select.getAttribute('aria-hidden') !== original.ariaHidden) select.setAttribute('aria-hidden', original.ariaHidden);
    sizeSourceAttributes.delete(select);
  }

  function enhanceSingleSize(wrapper) {
    const selects = [...wrapper.querySelectorAll('[data-fs-size-standard],[data-fs-size-premium]')];
    const activeSelects = selects.filter(select => !select.hidden && !select.disabled);
    const validOptions = select => [...select.options].filter(option => {
      const value = option.value.trim();
      const label = option.textContent.trim();
      return !option.disabled && value !== '' && !option.hasAttribute('data-placeholder') && !/^(?:please\s+)?(?:choose|select)(?:\s+(?:a\s+)?size)?$/i.test(label);
    });
    const canCompact = activeSelects.length > 0 && activeSelects.every(select => {
      const options = validOptions(select);
      return options.length === 1 && select.selectedOptions[0] === options[0];
    });

    if (!canCompact) {
      if (wrapper.hasAttribute(PROCESSED_SIZE)) {
        wrapper.removeAttribute(PROCESSED_SIZE);
        wrapper.classList.remove('fs-native-single-size');
        selects.forEach(select => {
          select.classList.remove('fs-native-single-size__source');
          restoreSizeSourceAccessibility(select);
        });
        wrapper.querySelector('.fs-native-size-summary')?.remove();
      }
      return;
    }
    const guide = wrapper.querySelector('[data-fs-guide-open],[data-fs-size-guide-open]');
    if (!guide) return;

    if (!wrapper.hasAttribute(PROCESSED_SIZE)) {
      wrapper.setAttribute(PROCESSED_SIZE, '');
      wrapper.classList.add('fs-native-single-size');
      selects.forEach(select => {
        select.classList.add('fs-native-single-size__source');
        makeSizeSourceAccessibleToSummary(select);
      });
    }

    let summary = wrapper.querySelector('.fs-native-size-summary');
    if (!summary) {
      summary = document.createElement('span');
      summary.className = 'fs-native-size-summary';
      summary.setAttribute('aria-live', 'polite');
      const context = document.createElement('span');
      context.className = 'fs-native-choice-context';
      context.textContent = 'Size';
      const value = document.createElement('span');
      value.className = 'fs-native-size-summary__value';
      summary.append(context, document.createTextNode(' '), value);
      wrapper.insertBefore(summary, guide);
    }
    updateSizeSummary(wrapper, summary);
  }

  function colourInputs(palette) {
    return [...palette.querySelectorAll('input[type="radio"][data-fs-colour],input[type="radio"][name="properties[BACKGROUND COLOUR]"]')];
  }

  function paletteMeasureKey(palette, labels, button) {
    const style = global.getComputedStyle(palette);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    const width = palette.clientWidth || palette.getBoundingClientRect().width;
    const buttonWidth = button?.getBoundingClientRect().width || parseFloat(global.getComputedStyle(button).width) || 0;
    const labelWidths = labels.map(label => {
      const rendered = label.getBoundingClientRect().width;
      return rendered || parseFloat(global.getComputedStyle(label).width) || 0;
    });
    return {key:[width, buttonWidth, gap, ...labelWidths].join('|'), width, buttonWidth, gap, labelWidths};
  }

  function applyPaletteVisibility(palette, expanded, button) {
    const inputs = colourInputs(palette);
    if (inputs.length <= 6) return;
    const labels = inputs.map(input => input.closest('label')).filter(Boolean);
    const selectedIndex = inputs.findIndex(input => input.checked);
    const measure = paletteMeasureKey(palette, labels, button);
    let visible = new Set();
    if (expanded) {
      labels.forEach((_, index) => visible.add(index));
    } else if (measure.width <= 0) {
      // A hidden/unlaid-out form must remain usable; wait for ResizeObserver
      // to provide real dimensions before collapsing any choices.
      labels.forEach((_, index) => visible.add(index));
    } else {
      let slots = 0;
      for (let count = 1; count <= Math.min(6, labels.length); count += 1) {
        const used = measure.labelWidths.slice(0, count).reduce((sum, width) => sum + width, 0)
          + measure.buttonWidth + measure.gap * count;
        if (used <= measure.width + 0.5) slots = count;
      }
      // At exceptionally narrow widths, retain at least the selected/first
      // swatch and allow normal flex wrapping rather than hiding it.
      slots = Math.max(1, slots);
      if (selectedIndex >= slots) {
        for (let index = 0; index < slots - 1; index += 1) visible.add(index);
        visible.add(selectedIndex);
      } else {
        for (let index = 0; index < Math.min(slots, labels.length); index += 1) visible.add(index);
      }
    }
    labels.forEach((label, index) => {
      const collapsed = !visible.has(index);
      label.classList.toggle('fs-native-colour-option--collapsed', collapsed);
      label.toggleAttribute('data-fs-native-colour-collapsed', collapsed);
    });
    paletteMeasureKeys.set(palette, measure.key);
  }

  function observePaletteSize(palette, button) {
    if (!global.ResizeObserver || paletteResizeObservers.has(palette)) return;
    const observer = new global.ResizeObserver(() => {
      const inputs = colourInputs(palette);
      const labels = inputs.map(input => input.closest('label')).filter(Boolean);
      const next = paletteMeasureKey(palette, labels, button).key;
      if (paletteMeasureKeys.get(palette) === next) return;
      applyPaletteVisibility(palette, button.getAttribute('aria-expanded') === 'true', button);
    });
    observer.observe(palette);
    paletteResizeObservers.set(palette, observer);
  }

  function updatePalette(palette, button) {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(expanded));
    const label = expanded ? 'Fewer colours' : 'More colours';
    if (button.textContent !== label) button.textContent = label;
    button.dataset.fsNativeColoursExpanded = String(expanded);
    applyPaletteVisibility(palette, expanded, button);
  }

  function enhancePalette(palette) {
    if (palette.hasAttribute(PROCESSED_PALETTE)) {
      const button = palette.querySelector(':scope > .fs-native-colour-more');
      if (button) applyPaletteVisibility(palette, button.getAttribute('aria-expanded') === 'true', button);
      return;
    }
    const inputs = colourInputs(palette);
    if (inputs.length <= 6 || !palette.matches(PALETTE_SELECTOR)) return;
    palette.setAttribute(PROCESSED_PALETTE, '');
    palette.classList.add('fs-native-colours');
    if (!palette.id) palette.id = uniqueId('fs-native-colours');

    let button = palette.querySelector(':scope > .fs-native-colour-more');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'fs-native-colour-more';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', palette.id);
      button.textContent = 'More colours';
      palette.append(button);
    }
    button.addEventListener('click', () => {
      button.setAttribute('aria-expanded', String(button.getAttribute('aria-expanded') !== 'true'));
      updatePalette(palette, button);
    });
    palette.addEventListener('change', event => {
      if (event.target instanceof global.HTMLInputElement && event.target.matches('input[type="radio"][data-fs-colour],input[type="radio"][name="properties[BACKGROUND COLOUR]"]')) {
        applyPaletteVisibility(palette, button.getAttribute('aria-expanded') === 'true', button);
      }
    });
    observePaletteSize(palette, button);
    updatePalette(palette, button);
  }

  function enhanceForm(form) {
    if (!(form instanceof global.HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
    form.querySelectorAll('fieldset > legend').forEach(simplifyLegend);
    feeCandidates(form).forEach(hideFeeExplanation);
    form.querySelectorAll('.fs-hoodie__size,.fs-ownhoodie__size,.fs-snugrem__size').forEach(enhanceSingleSize);
    form.querySelectorAll(PALETTE_SELECTOR).forEach(enhancePalette);
    form.querySelectorAll('.fs-native-single-size').forEach(wrapper => {
      const summary = wrapper.querySelector('.fs-native-size-summary');
      if (summary) updateSizeSummary(wrapper, summary);
    });
  }

  function startFormObserver(form) {
    if (formObservers.has(form)) return;
    const observer = new global.MutationObserver(records => {
      if (!records.length) return;
      enhanceForm(form);
    });
    observer.observe(form, {
      childList:true,
      subtree:true,
      characterData:true,
      attributes:true,
      attributeFilter:['hidden','disabled'],
    });
    formObservers.set(form, observer);
    form.addEventListener('change', () => enhanceForm(form));
  }

  function normalizedText(node) {
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function headingTag(node, tag) {
    if (node.tagName.toLowerCase() === tag) return node;
    const replacement = document.createElement(tag);
    [...node.attributes].forEach(attribute => replacement.setAttribute(attribute.name, attribute.value));
    replacement.append(...node.childNodes);
    node.replaceWith(replacement);
    return replacement;
  }

  function enhanceDescriptions() {
    document.querySelectorAll(FORM_SELECTOR).forEach(form => {
      const productId = form.getAttribute('data-productid');
      if (!SNUG_DESCRIPTION_IDS.has(productId)) return;
      const owner = form.closest('[data-product-featured]');
      let product;
      try { product = JSON.parse(owner?.getAttribute('data-product-featured') || 'null'); } catch { return; }
      // Shopify's input named "id" shadows form.id in HTMLFormElement.
      if (!product || product.isMainProduct !== true || String(product.id) !== productId || product.formID !== `#${form.getAttribute('id')}` || typeof product.sectionId !== 'string') return;
      const root = document.getElementById(`t4s-tab-des${product.sectionId}`);
      if (!root?.matches(DESCRIPTION_SELECTOR) || root.closest('form') || descriptions.has(root)) return;
      descriptions.add(root);
      root.classList.add('fs-native-product-copy');

      // Legacy rich text stores blank lines in nested wrappers. Keep the nodes
      // and all text/media intact; hide only demonstrably empty filler.
      root.querySelectorAll('.sbl-description,.sbl-size-guide-container,.subl-product-description,p,h2,h3,h4').forEach(node => {
        if (normalizedText(node) || node.id || node.matches('a,[role],[aria-label]') || node.querySelector('img,picture,svg,video,audio,iframe,table,form,input,button,select,textarea,a,hr,canvas,details,[id],[role],[aria-label]')) return;
        node.classList.add('fs-native-copy-spacer');
      });

      const legal = [...root.querySelectorAll('p')].find(node => /^Disclaimer\./i.test(normalizedText(node)) && /faceswag/i.test(normalizedText(node)));
      if (legal) legal.classList.add('fs-native-copy-legal');
      const origin = [...root.querySelectorAll('h2,h3,h4')].find(node => normalizedText(node) === 'Fabric Country of Origin');
      if (origin) {
        const label = normalizedText(origin);
        let group = origin.parentElement;
        while (group && group !== root && normalizedText(group) === label) group = group.parentElement;
        if (group && group !== root && !group.querySelector('table,img,picture,video,iframe')) group.classList.add('fs-native-copy-origin');
      }

      const faq = [...root.querySelectorAll('h2,h3,h4')].find(node => normalizedText(node) === 'Frequently Asked Questions');
      if (!faq) return; // A product without FAQs keeps its existing content.
      const section = document.createElement('section');
      section.className = 'fs-native-copy-faq';
      faq.before(section);
      let node = faq;
      while (node && node !== legal) {
        if (node !== faq && node.nodeType === global.Node.ELEMENT_NODE && /^(H1|H2|H3)$/.test(node.tagName)) break;
        const next = node.nextSibling;
        section.append(node);
        node = next;
      }
      const title = headingTag(faq, 'h2');
      title.classList.add('fs-native-copy-faq__title');
      section.querySelectorAll(':scope > h4').forEach(question => headingTag(question, 'h3').classList.add('fs-native-copy-faq__question'));
    });
  }

  function scan(node) {
    if (!(node instanceof global.Element) && node !== document) return;
    const forms = new Set();
    if (node instanceof global.Element) {
      if (node.matches(FORM_SELECTOR)) forms.add(node);
      const closest = node.closest(FORM_SELECTOR);
      if (closest) forms.add(closest);
    }
    node.querySelectorAll?.(FORM_SELECTOR).forEach(form => forms.add(form));
    forms.forEach(form => {
      enhanceForm(form);
      startFormObserver(form);
    });
    if (forms.size || node === document || node.closest?.(DESCRIPTION_SELECTOR) || node.matches?.(DESCRIPTION_SELECTOR) || node.querySelector?.(DESCRIPTION_SELECTOR)) enhanceDescriptions();
  }

  function start() {
    if (started) return;
    started = true;
    scan(document);
    const observer = new global.MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(scan));
    });
    observer.observe(document.documentElement, {childList:true,subtree:true});
  }

  global.FSNativeProductPresentation = Object.freeze({start});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})(window, document);
